import {
  PROFILE_BIRTH_DATE_DAY_LENGTH,
  PROFILE_BIRTH_DATE_FORMAT,
  PROFILE_BIRTH_DATE_ISO_FORMAT,
  PROFILE_BIRTH_DATE_MAX_DAY,
  PROFILE_BIRTH_DATE_MAX_MONTH,
  PROFILE_BIRTH_DATE_MONTH_LENGTH,
  PROFILE_BIRTH_DATE_SEPARATOR,
  PROFILE_BIRTH_DATE_YEAR_LENGTH,
  PROFILE_MAX_AGE,
} from '@/constants/profile';
import { startOfDay } from '@/helpers/date';

const padDatePart = (value) => String(value).padStart(2, '0');

const padSegment = (segment, max) => (segment.length === 1 && Number(segment) * 10 > max ? `0${segment}` : segment);

const clampSegment = (segment, max) => (segment.length === 2 ? String(Math.min(Math.max(Number(segment), 1), max)).padStart(2, '0') : segment);

const normalizeTypedValue = (value) => {
  const isoDate = PROFILE_BIRTH_DATE_ISO_FORMAT.exec(value);
  if (!isoDate) return value.replace(/[-/]/g, PROFILE_BIRTH_DATE_SEPARATOR);

  const [, year, month, day] = isoDate;

  return [day.padStart(2, '0'), month.padStart(2, '0'), year].join(PROFILE_BIRTH_DATE_SEPARATOR);
};

const splitBirthDateSegments = (value, clamp) => {
  const [typedDay = '', typedMonth = '', ...rest] = normalizeTypedValue(value)
    .split(PROFILE_BIRTH_DATE_SEPARATOR)
    .map((segment) => segment.replace(/\D/g, ''));
  const typedYear = rest.join('');

  const dayOverflow = typedMonth ? '' : typedDay.slice(PROFILE_BIRTH_DATE_DAY_LENGTH);
  const monthDigits = dayOverflow + typedMonth;
  const monthOverflow = typedYear ? '' : monthDigits.slice(PROFILE_BIRTH_DATE_MONTH_LENGTH);

  const fit = (segment, max) => (clamp ? clampSegment(padSegment(segment, max), max) : padSegment(segment, max));

  return [
    fit(typedDay.slice(0, PROFILE_BIRTH_DATE_DAY_LENGTH), PROFILE_BIRTH_DATE_MAX_DAY),
    fit(monthDigits.slice(0, PROFILE_BIRTH_DATE_MONTH_LENGTH), PROFILE_BIRTH_DATE_MAX_MONTH),
    (monthOverflow + typedYear).slice(0, PROFILE_BIRTH_DATE_YEAR_LENGTH),
  ];
};

const getSegmentStart = (segments, index) => segments.slice(0, index).reduce((start, segment) => start + segment.length + 1, 0);

export const formatBirthDateInput = (value, clamp = false) => {
  const segments = splitBirthDateSegments(value, clamp);
  const lastFilledIndex = segments.reduce((last, segment, index) => (segment ? index : last), -1);

  return segments.slice(0, lastFilledIndex + 1).join(PROFILE_BIRTH_DATE_SEPARATOR);
};

export const applyBirthDateInput = (value, caret) => {
  const isAppending = caret === value.length;
  const formatted = formatBirthDateInput(value, isAppending);

  if (isAppending) return { value: formatted, caret: formatted.length };

  const rawSegments = value.split(PROFILE_BIRTH_DATE_SEPARATOR);
  const segments = formatted.split(PROFILE_BIRTH_DATE_SEPARATOR);

  const index = Math.min(value.slice(0, caret).split(PROFILE_BIRTH_DATE_SEPARATOR).length - 1, segments.length - 1);
  const offset = caret - getSegmentStart(rawSegments, index);
  const padding = segments[index].length - rawSegments[index].replace(/\D/g, '').length;
  const position = Math.min(offset + Math.max(padding, 0), segments[index].length);

  return { value: formatted, caret: getSegmentStart(segments, index) + position };
};

export const completeBirthDateYear = (value, now = new Date()) => {
  const segments = value.split(PROFILE_BIRTH_DATE_SEPARATOR);
  const [day, month, year] = segments;

  if (segments.length !== 3 || year.length !== 2) return value;

  const century = Number(year) <= now.getFullYear() % 100 ? 2000 : 1900;

  return [day, month, century + Number(year)].join(PROFILE_BIRTH_DATE_SEPARATOR);
};

export const parseBirthDate = (value) => {
  const match = PROFILE_BIRTH_DATE_FORMAT.exec(value);
  if (!match) return null;

  const [, day, month, year] = match.map(Number);
  const date = new Date(year, month - 1, day);
  const isRealDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

  return isRealDate ? date : null;
};

export const isPastBirthDate = (date, now = new Date()) => startOfDay(date).getTime() < startOfDay(now).getTime();

export const getProfileAge = (birthDate, now = new Date()) => {
  const years = now.getFullYear() - birthDate.getFullYear();
  const hadBirthday = now.getMonth() > birthDate.getMonth() || (now.getMonth() === birthDate.getMonth() && now.getDate() >= birthDate.getDate());

  return hadBirthday ? years : years - 1;
};

export const validateBirthDate = (value) => {
  if (!PROFILE_BIRTH_DATE_FORMAT.test(value)) return 'invalidDate';

  const birthDate = parseBirthDate(value);
  if (!birthDate) return 'invalidDay';
  if (!isPastBirthDate(birthDate)) return 'birthDateInFuture';

  return getProfileAge(birthDate) > PROFILE_MAX_AGE ? 'birthDateTooOld' : null;
};

export const describeBirthDate = (value, languageTag) => {
  const birthDate = parseBirthDate(value);
  if (!birthDate) return null;

  const age = getProfileAge(birthDate);

  return {
    date: birthDate.toLocaleDateString(languageTag, { day: 'numeric', month: 'long', year: 'numeric' }),
    age,
    agePluralCategory: new Intl.PluralRules(languageTag).select(age),
  };
};

export const toApiBirthDate = (date) => `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
